/**
 * Purpose: VV-233 cart self-heal. The bag (drawer and cart page) shows the
 *          "Final sale" notice for configured brands, but a line added by a
 *          path the theme does not control (a cart that predates the feature,
 *          the third-party wishlist app's add button, a customer's persisted
 *          cart) can lack the "Final sale" line item property, so checkout
 *          would not show the note. For every such line this module stamps the
 *          property through the AJAX Cart API, so checkout always matches the
 *          bag. It also holds a checkout click until in-flight heals finish.
 *
 * Public API: custom element, rendered by snippets/cart-products.liquid only
 *             for lines whose vendor is on the final-sale list AND which lack
 *             the property:
 *               <vv-final-sale-heal hidden
 *                 data-key="<line item key>"
 *                 data-final-sale="<JSON object of properties to stamp>">
 *               </vv-final-sale-heal>
 *             data-final-sale comes from snippets/vv-final-sale.liquid
 *             (mode: 'json'), so the property key and value are never written
 *             in this file. A morph can also rewrite data-key on an element
 *             that stays connected (the cart morph matches id-less elements by
 *             position), so both attributes are observed and the new key is
 *             healed like a newly inserted marker. No JS exports.
 *
 * Upstream deps: Cart API GET Theme.routes.cart_url + '.js' and POST
 *                Theme.routes.cart_change_url (Theme is the inline global in
 *                snippets/scripts.liquid; falls back to /cart.js and
 *                /cart/change.js), @theme/utilities (fetchConfig),
 *                @theme/section-renderer (morphSection, the same re-render
 *                call assets/component-cart-items.js makes after a cart
 *                change), the public updateButtonStates() of
 *                <cart-quantity-selector-component> (called after the morph,
 *                as component-cart-items.js does), sessionStorage (optional).
 *
 * Downstream consumers: snippets/cart-products.liquid (loads this file as a
 *                       module next to component-cart-items.js; renders the
 *                       marker element inside .cart-items__variants-wrapper).
 *
 * Failure modes: never throws and never blocks the cart. A failed or
 *                rejected request only logs console.warn. On a page, a line
 *                key stays in an in-memory Set while it is queued, in flight
 *                or after it FAILED (request error, rejected change, or a
 *                change that left the line unstamped), so it is tried once
 *                per page load. Across page loads the tab session keeps a
 *                small counter per key in sessionStorage. It goes up when the
 *                change call is actually sent (never for the /cart.js read,
 *                so navigating away mid-read, or a failed read, is not
 *                counted) and the key is removed when the heal succeeds. A
 *                transient failure (429, timeout, navigation mid-change) is
 *                therefore retried on the next page load, and a key stops
 *                after MAX_FAILED_ATTEMPTS_PER_SESSION unsuccessful change
 *                calls in the tab session, so a persistent failure cannot
 *                loop. A failed /cart.js read is not counted, so it is
 *                retried once per page load (bounded by navigation). When an
 *                attempt succeeds, or the re-read shows the line gone or
 *                already stamped, the key is cleared (Set and counter):
 *                Shopify line keys are deterministic (variant + properties),
 *                so a later re-add of the same unstamped variant (wishlist
 *                app again, remove and re-add) arrives with the same key and
 *                must be healed again. MAX_STAMPS_PER_KEY caps change calls
 *                per key per page load as a second loop guard. The change
 *                call overwrites the whole properties object (Cart
 *                API) and treats a missing quantity as 1, so the line's
 *                current properties and quantity are re-read from /cart.js
 *                just before the call and re-sent: a heal keeps other
 *                properties and any earlier quantity edit. A +/- click that
 *                lands in the short window between that read and the change
 *                call can still be overwritten. A checkout click is held
 *                only while heals are pending, for at most CHECKOUT_WAIT_MS,
 *                then continues regardless. If a stamp makes a line identical
 *                to another line of the same variant, Shopify combines them,
 *                exactly as add-to-cart would.
 *
 * Performance: no work unless a marker exists. Per stale line: one GET
 *              /cart.js and one POST /cart/change (each capped at
 *              REQUEST_TIMEOUT_MS), strictly one line at a time, then one
 *              morph of each cart section (the drawer in 'hydration' mode).
 */
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

const STORAGE_KEY = 'vv-final-sale-heal:failed-attempts';
const STORAGE_LIMIT = 50;
const REQUEST_TIMEOUT_MS = 8000;
const CHECKOUT_WAIT_MS = 6000;
const MAX_STAMPS_PER_KEY = 3;
const MAX_FAILED_ATTEMPTS_PER_SESSION = 3;

/**
 * Line keys queued, in flight or FAILED on this page (the drawer and the cart page can render the same line).
 * A key leaves the Set when its heal succeeds, so a re-added line with the same key is healed again.
 */
const attemptedKeys = new Set();
/** Change calls made per line key on this page; capped by MAX_STAMPS_PER_KEY. */
const stampCounts = new Map();
/** Tail of the serial queue (one request at a time). Never rejects. */
let queueTail = Promise.resolve();
/** Heals queued or in flight. */
let pendingCount = 0;
/** True while a checkout click is waiting for the queue, so repeat clicks do not stack. */
let checkoutWaiting = false;

/**
 * @param {string} message
 * @param {unknown} [error]
 */
function warn(message, error) {
  console.warn(`[vv-final-sale-heal] ${message}`, error ?? '');
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isNonBlank(value) {
  return String(value ?? '').trim() !== '';
}

/**
 * @param {unknown} properties - A cart line's properties (an object, null or {} when it has none).
 * @param {Record<string, string>} finalSale - The properties a stamped line must carry.
 * @returns {boolean}
 */
function hasFinalSale(properties, finalSale) {
  const existing = isPlainObject(properties) ? properties : {};
  return Object.keys(finalSale).every((name) => isNonBlank(existing[name]));
}

/**
 * Parses the marker's data-final-sale JSON. Returns null unless it is a
 * non-empty object with non-blank values, so a malformed marker does nothing.
 * @param {string | undefined} raw
 * @returns {Record<string, string> | null}
 */
function parseFinalSale(raw) {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!isPlainObject(parsed)) return null;

    const entries = Object.entries(parsed)
      .filter(([, value]) => isNonBlank(value))
      .map(([name, value]) => [name, String(value)]);

    return entries.length > 0 ? Object.fromEntries(entries) : null;
  } catch (error) {
    return null;
  }
}

/** @returns {Record<string, number>} Unsuccessful change calls sent per line key in this tab session. */
function readFailureCounts() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
    return isPlainObject(parsed) ? /** @type {Record<string, number>} */ (parsed) : {};
  } catch (error) {
    return {};
  }
}

/** @param {string} key */
function failedAttemptsInSession(key) {
  const count = Number(readFailureCounts()[key]);
  return Number.isFinite(count) ? count : 0;
}

/**
 * @param {Record<string, number>} counts
 * @param {string} key
 * @returns {Record<string, number>} A copy without `key`.
 */
function withoutKey(counts, key) {
  return Object.fromEntries(Object.entries(counts).filter(([existing]) => existing !== key));
}

/** @param {(counts: Record<string, number>) => Record<string, number>} update */
function updateFailureCounts(update) {
  try {
    const entries = Object.entries(update(readFailureCounts())).slice(-STORAGE_LIMIT);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch (error) {
    // Storage unavailable (private mode, quota): the in-memory Set still dedupes this page view.
  }
}

/**
 * Counts a change call as sent for this key. The count stays until the heal succeeds, so it is the number of
 * unsuccessful calls. Called only right before the POST goes out: a navigation during the /cart.js read, or a
 * failed read, is never counted.
 * @param {string} key
 */
function countSentAttemptInSession(key) {
  const count = failedAttemptsInSession(key) + 1;
  updateFailureCounts((counts) => ({ ...withoutKey(counts, key), [key]: count }));
}

/**
 * Forgets a key (page Set and session counter) once its line is healed, gone or already stamped.
 * @param {string} key
 */
function clearAttempted(key) {
  attemptedKeys.delete(key);
  updateFailureCounts((counts) => withoutKey(counts, key));
}

/** @returns {Record<string, string>} */
function themeRoutes() {
  return typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
}

/**
 * fetch + JSON with a timeout, so a hung request cannot hold the serial queue.
 * Rejects on a non-OK status.
 * @param {string} url
 * @param {RequestInit} init
 * @returns {Promise<any>}
 */
async function requestJson(url, init) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    if (!response.ok) throw new Error(`${init.method ?? 'GET'} ${url} responded ${response.status}`);

    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

/** @returns {HTMLElement[]} Cart item components (drawer and cart page), one per rendered cart section. */
function cartItemsComponents() {
  /** @type {Set<string>} */
  const seen = new Set();

  return [...document.querySelectorAll('cart-items-component[data-section-id]')].filter((component) => {
    const { sectionId } = /** @type {HTMLElement} */ (component).dataset;
    if (!sectionId || seen.has(sectionId)) return false;

    seen.add(sectionId);
    return true;
  });
}

/**
 * Re-renders the cart sections from a change.js response, with the same
 * morphSection call component-cart-items.js makes (drawer: 'hydration', cart
 * page: 'full'). It deliberately does not dispatch cart:update: cart-drawer.js
 * listens to that event and, with auto-open on, would pop the drawer open on a
 * background page load. A property stamp changes no count or price, so no
 * other cart:update listener has anything to refresh. Afterwards the quantity
 * selectors' button states are restored, as component-cart-items.js does
 * after each of its morphs (its helper is private, so the public
 * updateButtonStates() of each <cart-quantity-selector-component> is called).
 * @param {Record<string, string> | undefined} sections
 */
async function renderSections(sections) {
  if (!sections) return;

  for (const component of cartItemsComponents()) {
    const sectionId = component.dataset.sectionId ?? '';
    const html = sections[sectionId];
    if (!html) continue;

    try {
      await morphSection(sectionId, html, component.dataset.drawer !== undefined ? 'hydration' : 'full');
    } catch (error) {
      warn(`could not re-render section ${sectionId}`, error);
    }
  }

  for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
    try {
      /** @type {any} */ (selector).updateButtonStates?.();
    } catch (error) {
      warn('could not restore quantity button states', error);
    }
  }
}

/**
 * Stamps `finalSale` onto the cart line with this key.
 * @param {string} key - Line item key.
 * @param {Record<string, string>} finalSale - Properties to stamp.
 */
async function healLine(key, finalSale) {
  const routes = themeRoutes();
  const cart = await requestJson(`${routes.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });

  // Re-read the line instead of trusting the render-time markup: the customer may have changed the quantity
  // since, and change.js overwrites the whole properties object and treats a missing quantity as 1.
  const line = Array.isArray(cart?.items) ? cart.items.find((item) => item.key === key) : null;
  if (!line || hasFinalSale(line.properties, finalSale)) {
    // Gone, or already stamped by another path: nothing to do, and the key is free for a later re-add.
    clearAttempted(key);
    return;
  }

  const existing = isPlainObject(line.properties) ? line.properties : {};
  const sectionIds = cartItemsComponents().map((component) => component.dataset.sectionId);
  const body = JSON.stringify({
    id: key,
    quantity: line.quantity,
    properties: { ...existing, ...finalSale },
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });

  stampCounts.set(key, (stampCounts.get(key) ?? 0) + 1);
  countSentAttemptInSession(key);
  const result = await requestJson(routes.cart_change_url || '/cart/change.js', fetchConfig('json', { body }));

  // A change that left this key unstamped did not stick: leave the key recorded (failed) so it cannot loop.
  const stuck =
    Array.isArray(result?.items) &&
    result.items.some((item) => item.key === key && !hasFinalSale(item.properties, finalSale));
  if (stuck) throw new Error('the line is still missing the final-sale property after the change call');

  // Stamped. Line keys are deterministic (variant + properties), so a later re-add of the same unstamped
  // variant arrives with this same key and must be healed again: forget the key.
  clearAttempted(key);
  await renderSections(result?.sections);
}

/**
 * Adds a line to the serial queue unless it is queued, in flight or failed on this page, has used up its failed
 * attempts in this tab session, or is over its per-page cap.
 * @param {string} key
 * @param {Record<string, string>} finalSale
 */
function enqueueHeal(key, finalSale) {
  if (attemptedKeys.has(key) || failedAttemptsInSession(key) >= MAX_FAILED_ATTEMPTS_PER_SESSION) return;
  if ((stampCounts.get(key) ?? 0) >= MAX_STAMPS_PER_KEY) return;

  attemptedKeys.add(key);
  pendingCount += 1;

  queueTail = queueTail
    .then(() => healLine(key, finalSale))
    .catch((error) => warn(`could not stamp the final-sale note on line ${key}`, error))
    .then(() => {
      pendingCount -= 1;
    });
}

/** Resolves once no heal is queued or in flight (including heals queued while waiting). */
async function drainQueue() {
  while (pendingCount > 0) {
    await queueTail;
  }
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLButtonElement | HTMLInputElement | null} The cart's checkout submit button, if the click was on one.
 */
function findCheckoutSubmitter(target) {
  if (!(target instanceof Element)) return null;

  const button = target.closest('[name="checkout"]');
  const isSubmitControl =
    (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) && button.type === 'submit';

  return isSubmitControl && button.form && !button.disabled ? button : null;
}

/** @param {HTMLButtonElement | HTMLInputElement} submitter */
function continueCheckout(submitter) {
  // The drawer morph keeps the button node, but fall back to a live one if it was replaced.
  const button = submitter.isConnected ? submitter : document.querySelector('button[name="checkout"]');
  const form = button instanceof HTMLButtonElement || button instanceof HTMLInputElement ? button.form : null;
  if (!button || !form) return;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit(button);
    return;
  }

  // Browsers without requestSubmit: form.submit() drops the submitter, so carry its name/value (Shopify
  // needs the `checkout` parameter to redirect to checkout instead of reloading the cart).
  const carrier = document.createElement('input');
  carrier.type = 'hidden';
  carrier.name = button.name;
  carrier.value = button.value;
  form.append(carrier);
  form.submit();
}

/**
 * Capture-phase click handler: while heals are pending, hold a click on the
 * cart's checkout submit until the queue drains (or CHECKOUT_WAIT_MS), then
 * submit the same form with the same submitter. Does nothing when idle, and
 * never matches accelerated checkout buttons (their shadow DOM is closed, so
 * the click is retargeted to the host element, which has no name="checkout").
 * @param {Event} event
 */
function onCheckoutClick(event) {
  if (pendingCount === 0 && !checkoutWaiting) return;

  const submitter = findCheckoutSubmitter(event.target);
  if (!submitter) return;

  event.preventDefault();
  if (checkoutWaiting) return;

  checkoutWaiting = true;
  let timer = 0;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, CHECKOUT_WAIT_MS);
  });

  Promise.race([drainQueue(), timeout])
    .catch((error) => warn('checkout hold failed, continuing', error))
    .finally(() => {
      clearTimeout(timer);
      checkoutWaiting = false;
    })
    .then(() => continueCheckout(submitter));
}

class VVFinalSaleHeal extends HTMLElement {
  /** A morph can rewrite these on an element that stays connected, so connectedCallback alone would miss it. */
  static observedAttributes = ['data-key', 'data-final-sale'];

  connectedCallback() {
    this.#enqueue();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Not connected yet: connectedCallback handles it. Dedupe and the caps still apply to a re-keyed marker.
    if (this.isConnected && oldValue !== newValue) this.#enqueue();
  }

  #enqueue() {
    const key = this.dataset.key;
    const finalSale = parseFinalSale(this.dataset.finalSale);
    if (!key || !finalSale) return;

    enqueueHeal(key, finalSale);
  }
}

if (!customElements.get('vv-final-sale-heal')) {
  customElements.define('vv-final-sale-heal', VVFinalSaleHeal);
  document.addEventListener('click', onCheckoutClick, true);
}
